import { Fragment, useEffect, useRef, useState } from 'react'
import { useSearchParams, type SetURLSearchParams } from 'react-router-dom'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import {
  listHistory,
  getKontoAufschlag,
  PRICE_ROLLOUT_PORTALS,
  type HistoryParams,
  type PriceHistoryEntry,
} from '@/api/priceRollout'
import { Panel } from '@/components/Panel'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { fmtEUR } from '@/lib/pricing-format'
import { historyGrundText, kontoKurz, portalText, writerText } from '@/lib/rollout-klartext'
import { ArtikelKennung } from '@/components/rollout/RolloutAnzeige'

const fallbackKonten = ['ideal-deko', 'b2c-trends', 'drs_e-commerce', '24-trends']
const berlin = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})
const tag = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  weekday: 'long',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})
const uhr = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})
const zeit = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})
const datum = (d: Date) => berlin.format(d)
function tageZurueck(n: number) {
  const d = new Date(`${datum(new Date())}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() - n)
  return d.toISOString().slice(0, 10)
}
const statusText: Record<NonNullable<PriceHistoryEntry['status']>, string> = {
  portal_bestaetigt: 'Am Portal bestätigt',
  dr_gesetzt: 'In DR gesetzt – Portal noch nicht bestätigt',
  ueberholt: 'Überholt',
  abweichend: 'Abweichend',
  unbekannt: 'Noch nicht bestätigt',
  protokoll: 'Nur Protokoll',
  fehlgeschlagen: 'Fehlgeschlagen',
}
function StatusChip({ h }: { h: PriceHistoryEntry }) {
  const s = h.status ?? (h.success ? 'unbekannt' : 'fehlgeschlagen')
  const farbe =
    s === 'portal_bestaetigt'
      ? 'bg-success-soft text-success'
      : s === 'dr_gesetzt'
        ? 'bg-primary-soft text-primary'
        : s === 'abweichend'
          ? 'bg-warn-soft text-warn'
          : s === 'fehlgeschlagen'
            ? 'bg-coral-soft text-coral'
            : 'bg-panel-2 text-muted'
  return (
    <>
      <Badge
        variant="outline"
        className={`whitespace-normal ${farbe}`}
        title={
          s === 'portal_bestaetigt' && h.ist_stand
            ? `Ernte ${zeit.format(new Date(h.ist_stand))}`
            : undefined
        }
      >
        {statusText[s]}
        {s === 'abweichend' ? ` – jetzt ${fmtEUR(h.ist_jetzt)}` : ''}
      </Badge>
      {h.error && (
        <div className={`text-xs ${h.success ? 'text-ink-2' : 'text-coral'}`}>{h.error}</div>
      )}
    </>
  )
}
function HistoryFilter({
  params,
  setParams,
  set,
  suche,
  sucheAendern,
  timer,
  setSuche,
  von,
  bis,
  art,
  konten,
  changed,
}: {
  params: URLSearchParams
  setParams: SetURLSearchParams
  set: (values: Record<string, string>) => void
  suche: string
  sucheAendern: (value: string) => void
  timer: React.RefObject<ReturnType<typeof setTimeout> | null>
  setSuche: (value: string) => void
  von: string
  bis: string
  art: NonNullable<HistoryParams['art']>
  konten: string[]
  changed: boolean
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 mb-4">
      <label className="text-xs text-ink-2">
        Suche
        <Input
          type="search"
          aria-label="Historie durchsuchen"
          placeholder="Suchen: *367, DR-10015 oder 2808"
          value={suche}
          onChange={(e) => sucheAendern(e.target.value)}
        />
      </label>
      <label className="text-xs text-ink-2">
        Portal
        <select
          aria-label="Portal"
          className="block h-9 rounded border border-line bg-panel px-2 focus-visible:outline-2 focus-visible:outline-primary"
          value={params.get('portal') ?? ''}
          onChange={(e) =>
            set({
              portal: e.target.value,
              ...(e.target.value && e.target.value !== 'ebay' ? { konto: '' } : {}),
            })
          }
        >
          <option value="">Alle Portale</option>
          {PRICE_ROLLOUT_PORTALS.map((p) => (
            <option key={p} value={p}>
              {portalText(p)}
            </option>
          ))}
        </select>
      </label>
      {(!params.get('portal') || params.get('portal') === 'ebay') && (
        <label className="text-xs text-ink-2">
          Konto
          <select
            aria-label="Konto"
            className="block h-9 rounded border border-line bg-panel px-2 focus-visible:outline-2 focus-visible:outline-primary"
            value={params.get('konto') ?? ''}
            onChange={(e) => set({ konto: e.target.value })}
          >
            <option value="">Alle Konten</option>
            {konten.map((k) => (
              <option key={k} value={k}>
                {kontoKurz(k)}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="flex flex-wrap gap-1" aria-label="Zeitraum">
        {(
          [
            ['Heute', 0],
            ['7 Tage', 6],
            ['30 Tage', 29],
            ['Alles', -1],
          ] as const
        ).map(([label, n]) => (
          <Button
            key={label}
            size="sm"
            aria-pressed={
              von === (n < 0 ? '' : tageZurueck(n)) && bis === (n < 0 ? '' : datum(new Date()))
            }
            variant={
              von === (n < 0 ? '' : tageZurueck(n)) && bis === (n < 0 ? '' : datum(new Date()))
                ? 'default'
                : 'outline'
            }
            onClick={() =>
              set({ von: n < 0 ? '' : tageZurueck(n), bis: n < 0 ? '' : datum(new Date()) })
            }
          >
            {label}
          </Button>
        ))}
      </div>
      <label className="text-xs text-ink-2">
        Von
        <Input
          type="date"
          aria-label="Von"
          value={von}
          onChange={(e) => set({ von: e.target.value, bis })}
        />
      </label>
      <label className="text-xs text-ink-2">
        Bis
        <Input
          type="date"
          aria-label="Bis"
          value={bis}
          onChange={(e) => set({ von, bis: e.target.value })}
        />
      </label>
      <label className="text-xs text-ink-2">
        Art
        <select
          aria-label="Art"
          value={art}
          onChange={(e) => set({ art: e.target.value })}
          className="block h-9 rounded border border-line bg-panel px-2 focus-visible:outline-2 focus-visible:outline-primary"
        >
          <option value="aenderungen">Preisänderungen</option>
          <option value="alle">Alle Ereignisse</option>
          <option value="fehler">Fehler</option>
        </select>
      </label>
      <label className="text-xs text-ink-2">
        Richtung
        <select
          aria-label="Richtung"
          value={params.get('richtung') ?? ''}
          onChange={(e) => set({ richtung: e.target.value })}
          className="block h-9 rounded border border-line bg-panel px-2 focus-visible:outline-2 focus-visible:outline-primary"
        >
          <option value="">Alle</option>
          <option value="hoch">↑ teurer</option>
          <option value="runter">↓ günstiger</option>
        </select>
      </label>
      {changed && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (timer.current) clearTimeout(timer.current)
            setSuche('')
            const p = new URLSearchParams(params)
            for (const k of ['sku', 'portal', 'konto', 'von', 'bis', 'art', 'richtung']) p.delete(k)
            setParams(p)
          }}
        >
          Filter zurücksetzen
        </Button>
      )}
    </div>
  )
}

function PreisUebergang({ h }: { h: PriceHistoryEntry }) {
  return (
    <>
      {fmtEUR(h.old_price)} → {fmtEUR(h.new_price)}
      {h.delta != null && h.delta !== 0 && (
        <div>
          <Badge
            variant="outline"
            className={h.delta > 0 ? 'bg-success-soft text-success' : 'bg-coral-soft text-coral'}
          >
            {h.delta > 0 ? '+' : '−'}
            {fmtEUR(Math.abs(h.delta))}
          </Badge>
        </div>
      )}
    </>
  )
}

function HistoryZeile({ h }: { h: PriceHistoryEntry }) {
  return (
    <tr className="border-t border-line-2 align-top">
      <td className="py-2">{uhr.format(new Date(h.applied_at))}</td>
      <td className="py-2">
        <ArtikelKennung kennung={h.kennung} sku={h.sku ?? String(h.stock_item_id)} kompakt />
      </td>
      <td className="py-2">
        {portalText(h.portal)}{' '}
        {(h.ebay_konto || h.account) && (
          <span className="whitespace-nowrap rounded bg-panel-2 px-1 text-xs">
            {kontoKurz((h.ebay_konto || h.account)!)}
          </span>
        )}
      </td>
      <td className="py-2 text-right font-mono tabular-nums whitespace-nowrap">
        <PreisUebergang h={h} />
      </td>
      <td className="py-2 text-right font-mono tabular-nums whitespace-nowrap">
        {fmtEUR(h.target_price)}
        {(h.konto_aufschlag_ct ?? 0) > 0 && (
          <div className="font-sans text-xs text-muted">
            inkl. +{h.konto_aufschlag_ct} ct {h.ebay_konto ? kontoKurz(h.ebay_konto) : ''}
          </div>
        )}
      </td>
      <td className="py-2">
        <StatusChip h={h} />
      </td>
      <td className="py-2 text-xs">{historyGrundText(h.reason)}</td>
      <td className="py-2 text-xs">{writerText(h.writer)}</td>
      <td className="py-2">
        {h.angebote?.map((a) => (
          <a
            key={a.url}
            href={a.url}
            target="_blank"
            rel="noreferrer"
            className="block text-xs text-primary focus-visible:outline-2 focus-visible:outline-primary"
          >
            {a.konto ? `${kontoKurz(a.konto)} ` : ''}
            {a.nummer}
          </a>
        ))}
      </td>
    </tr>
  )
}

export function HistoryTab() {
  const [params, setParams] = useSearchParams()
  const urlSku = params.get('sku') ?? ''
  const [suche, setSuche] = useState(urlSku)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { data: kontoDaten } = useQuery({
    queryKey: ['konto-aufschlag'],
    queryFn: getKontoAufschlag,
  })
  const konten = kontoDaten?.konten.map((k) => k.account) ?? fallbackKonten
  // Adresswechsel gewinnt auch dann, wenn ein Suchlauf noch aussteht.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current)
    setSuche(urlSku)
  }, [urlSku])
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )
  const sucheAendern = (wert: string) => {
    setSuche(wert)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      setParams(
        (p) => {
          const next = new URLSearchParams(p)
          if (wert.trim()) next.set('sku', wert.trim())
          else next.delete('sku')
          return next
        },
        { replace: true },
      )
    }, 300)
  }
  const von = params.has('von') ? (params.get('von') ?? '') : tageZurueck(29)
  const bis = params.has('bis') ? (params.get('bis') ?? '') : datum(new Date())
  const art = (params.get('art') || 'aenderungen') as NonNullable<HistoryParams['art']>
  const filter: HistoryParams = {
    ...(params.get('sku')?.trim() ? { sku: params.get('sku')!.trim() } : {}),
    ...(params.get('portal') ? { portal: params.get('portal')! } : {}),
    ...(params.get('konto') ? { konto: params.get('konto')! } : {}),
    ...(von ? { von } : {}),
    ...(bis ? { bis } : {}),
    art,
    ...(params.get('richtung')
      ? { richtung: params.get('richtung') as HistoryParams['richtung'] }
      : {}),
  }
  const set = (values: Record<string, string>) => {
    const p = new URLSearchParams(params)
    for (const [k, v] of Object.entries(values)) {
      if (v || k === 'von' || k === 'bis') p.set(k, v)
      else p.delete(k)
    }
    setParams(p)
  }
  const q = useInfiniteQuery({
    queryKey: ['rollout-history', filter],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      listHistory({ ...filter, ...(pageParam ? { vor: pageParam } : {}) }),
    getNextPageParam: (page) => page.naechster ?? undefined,
  })
  const rows = q.data?.pages.flatMap((p) => p.history) ?? [],
    summary = q.data?.pages[0]?.zusammenfassung
  const groups = new Map<string, PriceHistoryEntry[]>()
  for (const h of rows) {
    const key = datum(new Date(h.applied_at))
    groups.set(key, [...(groups.get(key) ?? []), h])
  }
  const changed = ['sku', 'portal', 'konto', 'von', 'bis', 'art', 'richtung'].some((k) =>
    params.has(k),
  )
  return (
    <Panel title="Preis-Historie">
      <HistoryFilter
        params={params}
        setParams={setParams}
        set={set}
        suche={suche}
        sucheAendern={sucheAendern}
        timer={timer}
        setSuche={setSuche}
        von={von}
        bis={bis}
        art={art}
        konten={konten}
        changed={changed}
      />
      {summary && (
        <p className="mb-3 text-sm text-ink-2">
          {summary.anzahl}{' '}
          {art === 'alle' ? 'Ereignisse' : art === 'fehler' ? 'Fehler' : 'Preisänderungen'}
          {art !== 'fehler' && ` · ${summary.hoch} teurer · ${summary.runter} günstiger`}
        </p>
      )}
      {q.isLoading ? (
        <p>Lade Historie…</p>
      ) : q.isError && !q.data ? (
        <div>
          Historie konnte nicht geladen werden.{' '}
          <Button onClick={() => void q.refetch()}>Erneut laden</Button>
        </div>
      ) : rows.length === 0 ? (
        <p>
          Keine Preisänderung für diese Filter – Zeitraum erweitern oder ‚Alle Ereignisse‘ wählen.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-sm [&_td]:px-2 [&_th]:px-2">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th>Uhrzeit</th>
                <th>Artikel</th>
                <th>Portal</th>
                <th className="text-right">Vorher → Nachher</th>
                <th className="text-right">Ziel</th>
                <th>Status</th>
                <th>Auslöser</th>
                <th>Schreibweg</th>
                <th>Angebote</th>
              </tr>
            </thead>
            <tbody>
              {[...groups].map(([key, entries]) => (
                <Fragment key={key}>
                  <tr>
                    <th colSpan={9} className="bg-panel-2 py-2 text-left">
                      {key === tageZurueck(0)
                        ? 'Heute'
                        : key === tageZurueck(1)
                          ? 'Gestern'
                          : tag.format(new Date(entries[0]!.applied_at))}
                    </th>
                  </tr>
                  {entries.map((h) => (
                    <HistoryZeile key={h.id} h={h} />
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.isFetchNextPageError && (
        <div className="mt-3 text-coral">
          Weitere Ereignisse konnten nicht geladen werden.{' '}
          <Button onClick={() => void q.fetchNextPage()}>Erneut laden</Button>
        </div>
      )}
      {q.hasNextPage && !q.isFetchNextPageError && (
        <Button
          className="mt-3"
          onClick={() => void q.fetchNextPage()}
          disabled={q.isFetchingNextPage}
        >
          Weitere laden
        </Button>
      )}
    </Panel>
  )
}
